/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { useTheme } from '@mui/material';
import type { DataZoomComponentOption } from 'echarts';
import type { EChartsInstance, EChartsOption } from 'echarts-for-react';
import ReactECharts from 'echarts-for-react';
import React from 'react';
import { EChartsDarkTheme, EChartsLightTheme } from '@opengeoweb/theme';
import {
  createParameterNames,
  getOption,
  getZoomPreviewPlot,
  MIN_GRAPH_WIDTH,
  PLOT_HEIGHT,
} from './chartUtils';
import { CurrentTime, useCurrentTime } from './CurrentTime';
import { dateFormatter } from './dateFormatter';
import type { PlotWithData } from './types';
import {
  getVisibleTimeRange,
  useVerticalGridlines,
} from './useVerticalGridlines';
import { getGridlineInterval, getGridlineLabel } from './verticalGridlines';

// Patch for echarts-for-react ≤ 3.0.6: renderNewEcharts doesn't guard against
// this.ele becoming null when the component unmounts during async init.
interface EChartsReactCoreProto {
  renderNewEcharts?: () => Promise<void>;
  ele?: HTMLElement | null;
}
// Guard: mocked ReactECharts (forwardRef) has no real prototype chain.
const reactEChartsProto = (ReactECharts as { prototype?: unknown }).prototype;
const echartsReactCoreProto: EChartsReactCoreProto | null = reactEChartsProto
  ? (Object.getPrototypeOf(reactEChartsProto) as EChartsReactCoreProto | null)
  : null;
if (echartsReactCoreProto?.renderNewEcharts) {
  const originalRenderNewEcharts = echartsReactCoreProto.renderNewEcharts;
  echartsReactCoreProto.renderNewEcharts =
    async function patchedRenderNewEcharts(
      this: EChartsReactCoreProto & { ele: HTMLElement | null },
    ): Promise<void> {
      try {
        await originalRenderNewEcharts.call(this);
      } catch (err) {
        if (!this.ele) {
          return;
        }
        throw err;
      }
    };
}

interface EChartsLegendApi {
  getModel: () => {
    getComponent: (name: string) => unknown;
  };
  getViewOfComponentModel: (component: unknown) => {
    group: {
      getBoundingRect: () => { height: number };
    };
  };
}

export const useChartHeight = ({
  plotsWithData,
  shouldShowLegend,
  option,
  fitToContainer = false,
  shouldShowZoom = true,
  controlsOnly,
}: {
  plotsWithData: PlotWithData[];
  shouldShowLegend: boolean;
  option: EChartsOption;
  fitToContainer?: boolean;
  shouldShowZoom?: boolean;
  controlsOnly?: 'legend' | 'zoom';
}): {
  chartHeight: number;
  chartRef: React.RefObject<ReactECharts | null>;
  updateHeight: (echartsInstance: EChartsInstance) => void;
} => {
  const getDefaultHeight = React.useCallback(
    (plotsWithData: PlotWithData[]): number => {
      if (controlsOnly === 'legend') {
        return 32;
      }
      if (controlsOnly === 'zoom') {
        return 52;
      }
      return 100 + PLOT_HEIGHT * plotsWithData.length;
    },
    [controlsOnly],
  );
  const [chartHeight, setChartHeight] = React.useState(
    getDefaultHeight(plotsWithData),
  );

  const chartRef = React.useRef<ReactECharts>(null!);

  const updateHeight = React.useCallback(
    (echartsInstance: EChartsInstance): void => {
      if (echartsInstance.isDisposed()) {
        return;
      }
      // Use the current container size before measuring a wrapped legend.
      // This also clears explicit dimensions captured during async init.
      echartsInstance.resize({ width: 'auto', height: 'auto' });
      const legendApi = echartsInstance as unknown as EChartsLegendApi;
      if (legendApi.getModel() !== null) {
        const legend = legendApi.getModel().getComponent('legend');
        const legendView = legendApi.getViewOfComponentModel(legend);
        const { height } = legendView.group.getBoundingRect();

        const grid = option.grid ?? [];

        const gridArray = Array.isArray(grid) ? grid : [];

        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        const newGrid = gridArray.map((grid) => ({
          ...grid,
          top: shouldShowLegend
            ? `${parseInt(grid.top as string, 10) + height}px`
            : grid.top,
          ...(fitToContainer
            ? {
                top: shouldShowLegend ? 20 + height : 8,
                height: 'auto',
                bottom: shouldShowZoom ? 75 : 24,
              }
            : {}),
        }));

        echartsInstance.setOption({
          legend: {
            top: 0,
            height: shouldShowLegend ? height : 0,
            show: shouldShowLegend,
          },
          grid: controlsOnly ? gridArray : newGrid,
        });

        const newHeight =
          getDefaultHeight(plotsWithData) + (shouldShowLegend ? height : 0);

        if (!fitToContainer) {
          setChartHeight(
            controlsOnly === 'legend' ? Math.ceil(height) : newHeight,
          );
        }
      }
    },
    [
      option.grid,
      plotsWithData,
      shouldShowLegend,
      fitToContainer,
      controlsOnly,
      getDefaultHeight,
      shouldShowZoom,
    ],
  );

  React.useEffect(() => {
    const instance = chartRef.current?.getEchartsInstance();

    if (instance && !instance.isDisposed()) {
      instance.setOption({
        legend: {
          top: 0,
          show: shouldShowLegend,
        },
      });
      updateHeight(instance);
    }
  }, [shouldShowLegend, updateHeight]);

  React.useEffect(() => {
    if (
      (fitToContainer || controlsOnly) &&
      typeof ResizeObserver !== 'undefined'
    ) {
      return undefined;
    }
    const onHeight = (): void => {
      const instance = chartRef.current?.getEchartsInstance();
      if (instance && !instance.isDisposed()) {
        updateHeight(instance);
      }
    };

    window.addEventListener('resize', onHeight);

    return (): void => {
      window.removeEventListener('resize', onHeight);
    };
  }, [fitToContainer, controlsOnly, updateHeight]);

  return { chartHeight, chartRef, updateHeight };
};

export interface DataZoomState {
  start: number;
  end: number;
}

interface AxisPointerEvent {
  axesInfo?: { axisDim: string; axisIndex: number; value: number | string }[];
}

// Keep chart interaction state local to a view; Redux still owns plot configuration.
export interface ChartSynchronization {
  zoomState: React.RefObject<DataZoomState | null>;
  register: (instance: EChartsInstance, syncPointer?: boolean) => void;
  unregister: (instance: EChartsInstance) => void;
  zoom: (state: DataZoomState, source?: EChartsInstance) => void;
  pointer: (event: AxisPointerEvent, source?: EChartsInstance) => void;
}

export const useChartSynchronization = (): ChartSynchronization => {
  const instances = React.useRef(new Map<EChartsInstance, boolean>());
  const zoomState = React.useRef<DataZoomState | null>(null);
  return React.useMemo(
    () => ({
      zoomState,
      register: (instance: EChartsInstance, syncPointer = true): void => {
        instances.current.set(instance, syncPointer);
        if (zoomState.current && !instance.isDisposed()) {
          instance.dispatchAction(
            { type: 'dataZoom', ...zoomState.current },
            { silent: true },
          );
        }
      },
      unregister: (instance: EChartsInstance): void => {
        instances.current.delete(instance);
      },
      zoom: (state: DataZoomState, source?: EChartsInstance): void => {
        zoomState.current = state;
        instances.current.forEach((_syncPointer, instance) => {
          if (instance !== source && !instance.isDisposed()) {
            instance.dispatchAction(
              { type: 'dataZoom', ...state },
              { silent: true },
            );
          }
        });
      },
      pointer: (event: AxisPointerEvent, source?: EChartsInstance): void => {
        const axis = event.axesInfo?.find(({ axisDim }) => axisDim === 'x');
        instances.current.forEach((syncPointer, instance) => {
          if (syncPointer && instance !== source && !instance.isDisposed()) {
            instance.dispatchAction(
              axis
                ? {
                    type: 'updateAxisPointer',
                    x: instance.convertToPixel({ xAxisIndex: 0 }, axis.value),
                    y: instance.getHeight() / 2,
                    axesInfo: [
                      { axisDim: 'x', axisIndex: 0, value: axis.value },
                    ],
                  }
                : { type: 'updateAxisPointer', currTrigger: 'leave' },
              { silent: true },
            );
          }
        });
      },
    }),
    [],
  );
};

export const applyDataZoomState = (
  option: EChartsOption,
  dataZoomState: DataZoomState | null,
): EChartsOption => {
  if (!dataZoomState || !option?.dataZoom || !Array.isArray(option.dataZoom)) {
    return option;
  }
  return {
    ...option,
    dataZoom: option.dataZoom.map((dz: DataZoomComponentOption) => ({
      ...dz,
      start: dataZoomState.start,
      end: dataZoomState.end,
    })),
  };
};

export const extractDataZoomState = (params: {
  start?: number;
  end?: number;
  batch?: { start: number; end: number }[];
}): DataZoomState | null => {
  if (params.batch && params.batch.length > 0) {
    return {
      start: params.batch[0].start,
      end: params.batch[0].end,
    };
  }
  if (params.start !== undefined && params.end !== undefined) {
    return {
      start: params.start,
      end: params.end,
    };
  }
  return null;
};

export const TimeSeriesChart: React.FC<{
  plotsWithData: PlotWithData[];
  animation?: boolean;
  shouldShowLegend?: boolean;
  onToggleLegendParam?: (legendId: string) => void;
  fitToContainer?: boolean;
  shouldShowZoom?: boolean;
  controlsOnly?: 'legend' | 'zoom';
  synchronization?: ReturnType<typeof useChartSynchronization>;
  sharedTimesteps?: Date[];
  currentTime?: number;
}> = ({
  plotsWithData,
  animation = false,
  shouldShowLegend = true,
  onToggleLegendParam,
  fitToContainer = false,
  shouldShowZoom = true,
  controlsOnly,
  synchronization,
  sharedTimesteps,
  currentTime,
}) => {
  const now = useCurrentTime(
    currentTime === undefined && controlsOnly !== 'legend',
  );
  const [indicatorInstance, setIndicatorInstance] =
    React.useState<EChartsInstance | null>(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const containerRef = React.useRef<HTMLDivElement>(null);
  const registeredInstance = React.useRef<EChartsInstance | null>(null);

  React.useEffect(
    () => (): void => {
      if (registeredInstance.current) {
        synchronization?.unregister(registeredInstance.current);
      }
    },
    [synchronization],
  );

  const dataZoomStateRef = React.useRef<DataZoomState | null>(null);
  const formatZoomLabel = React.useCallback((timestamp: number): string => {
    const range = getVisibleTimeRange(registeredInstance.current);
    return range
      ? getGridlineLabel(timestamp, getGridlineInterval(...range))
          .filter(Boolean)
          .join(' ')
      : dateFormatter(timestamp);
  }, []);

  const lastLegendEventRef = React.useRef<unknown>(null);

  const enabledPlots = React.useMemo(
    () =>
      plotsWithData.filter(
        (plot) => plot.enabled === undefined || plot.enabled,
      ),
    [plotsWithData],
  );

  const option = React.useMemo((): EChartsOption => {
    const base = getOption(
      controlsOnly === 'zoom'
        ? [getZoomPreviewPlot(enabledPlots)]
        : enabledPlots,
      shouldShowLegend,
      sharedTimesteps,
      formatZoomLabel,
    );
    const dataZoom = Array.isArray(base.dataZoom) ? base.dataZoom : [];
    if (!controlsOnly) {
      return {
        ...base,
        dataZoom: dataZoom.map((zoom) => ({ ...zoom, show: shouldShowZoom })),
      };
    }
    return {
      ...base,
      tooltip: { show: false },
      axisPointer: { show: false },
      grid: (Array.isArray(base.grid) ? base.grid : []).map((grid) => ({
        ...grid,
        top: 0,
        height: 0,
      })),
      xAxis: (Array.isArray(base.xAxis) ? base.xAxis : []).map((axis) => ({
        ...axis,
        show: false,
      })),
      yAxis: (Array.isArray(base.yAxis) ? base.yAxis : []).map((axis) => ({
        ...axis,
        show: false,
      })),
      series: (Array.isArray(base.series) ? base.series : []).map((series) => ({
        ...series,
        // The slider needs real values to render its data preview. Hide the
        // supporting series rather than replacing their values with nulls.
        data: controlsOnly === 'legend' ? [] : series.data,
        ...(controlsOnly === 'zoom' && {
          lineStyle: { opacity: 0 },
          areaStyle: { opacity: 0 },
          itemStyle: { opacity: 0 },
          label: { show: false },
          showSymbol: false,
          emphasis: { disabled: true },
        }),
        silent: true,
      })),
      dataZoom:
        controlsOnly === 'zoom'
          ? dataZoom.map((zoom) => ({
              ...zoom,
              top: 4,
              bottom: undefined,
              showDataShadow: true,
            }))
          : [],
    };
  }, [
    enabledPlots,
    shouldShowLegend,
    shouldShowZoom,
    controlsOnly,
    sharedTimesteps,
    formatZoomLabel,
  ]);

  const { lines, updateGridlines } = useVerticalGridlines(
    controlsOnly ? 0 : enabledPlots.length,
  );
  const timeAxisStyle = (isDark ? EChartsDarkTheme : EChartsLightTheme)
    .timeAxis;
  const gridlineStyle = timeAxisStyle.splitLine.lineStyle;

  const optionWithZoom = React.useMemo(
    (): EChartsOption =>
      applyDataZoomState(
        option,
        synchronization?.zoomState.current ?? dataZoomStateRef.current,
      ),
    [option, synchronization],
  );

  const { chartHeight, chartRef, updateHeight } = useChartHeight({
    plotsWithData: enabledPlots,
    shouldShowLegend,
    option: optionWithZoom,
    fitToContainer,
    shouldShowZoom,
    controlsOnly,
  });
  const observesContainer =
    (fitToContainer || !!controlsOnly) && typeof ResizeObserver !== 'undefined';

  React.useEffect(() => {
    const container = containerRef.current;
    if (!observesContainer || !container) {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const instance = registeredInstance.current;
      if (
        instance &&
        !instance.isDisposed() &&
        container.clientWidth > 0 &&
        container.clientHeight > 0
      ) {
        updateHeight(instance);
      }
    });
    observer.observe(container);
    return (): void => observer.disconnect();
  }, [observesContainer, updateHeight]);

  return option ? (
    <div
      data-testid={
        controlsOnly
          ? { legend: 'TimeSeriesLegend', zoom: 'TimeSeriesZoom' }[controlsOnly]
          : 'TimeSeriesChart'
      }
      ref={containerRef}
      style={
        fitToContainer
          ? {
              position: 'relative',
              height: '100%',
              width: '100%',
              minHeight: 0,
            }
          : { position: 'relative' }
      }
    >
      <ReactECharts
        autoResize={!observesContainer}
        notMerge={true}
        onChartReady={(instance: EChartsInstance) => {
          if (registeredInstance.current) {
            synchronization?.unregister(registeredInstance.current);
          }
          registeredInstance.current = instance;
          if (controlsOnly !== 'legend') {
            synchronization?.register(instance, controlsOnly !== 'zoom');
          }
          updateHeight(instance);
          updateGridlines(instance);
          setIndicatorInstance(instance);
        }}
        // noop, prevent flickering when toggling legend
        onEvents={{
          finished: () => {},
          rendered: (_params: unknown, chart: EChartsInstance) => {
            updateGridlines(chart);
          },
          datazoom: (params: {
            start?: number;
            end?: number;
            batch?: { start: number; end: number }[];
          }) => {
            const newState = extractDataZoomState(params);
            if (newState) {
              dataZoomStateRef.current = newState;
              synchronization?.zoom(
                newState,
                registeredInstance.current ?? undefined,
              );
            }
          },
          updateAxisPointer: (event: AxisPointerEvent) => {
            if (controlsOnly) {
              return;
            }
            synchronization?.pointer(
              event,
              registeredInstance.current ?? undefined,
            );
          },
          legendselectchanged: (params: {
            name: string;
            selected: Record<string, boolean>;
          }) => {
            if (!option.legend) {
              return;
            }

            // Ignore duplicate deliveries of the same emit (see ref comment).
            if (lastLegendEventRef.current === params) {
              return;
            }
            lastLegendEventRef.current = params;

            const foundParam = createParameterNames(plotsWithData).find(
              (param) => param.name === params.name,
            );

            if (foundParam?.id) {
              onToggleLegendParam?.(foundParam.id);
            }
          },
        }}
        option={{
          ...optionWithZoom,
          backgroundColor: 'transparent',
          animation,
        }}
        ref={chartRef}
        style={{
          background: 'transparent',
          height: fitToContainer ? '100%' : chartHeight,
          minWidth: fitToContainer ? 0 : MIN_GRAPH_WIDTH,
        }}
        theme={isDark ? EChartsDarkTheme : EChartsLightTheme}
      />
      <svg
        aria-hidden="true"
        data-testid="TimeSeriesChart-gridlines"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {lines.map((line) => (
          <g key={`${line.top}-${line.x}`}>
            <line
              opacity={gridlineStyle.opacity}
              stroke={gridlineStyle.color[0]}
              strokeWidth={1}
              x1={line.x}
              x2={line.x}
              y1={line.top}
              y2={line.bottom}
            />
            {line.label ? (
              <>
                <line
                  stroke={timeAxisStyle.axisTick.lineStyle.color}
                  x1={line.x}
                  x2={line.x}
                  y1={line.bottom}
                  y2={line.bottom + 4}
                />
                <text
                  data-testid="TimeSeriesChart-timeLabel"
                  fill={timeAxisStyle.axisLabel.color}
                  fontSize={timeAxisStyle.axisLabel.fontSize}
                  textAnchor="middle"
                  x={line.x}
                  y={line.bottom + 12}
                >
                  <tspan x={line.x}>{line.label[0]}</tspan>
                  <tspan dy={10} x={line.x}>
                    {line.label[1]}
                  </tspan>
                </text>
              </>
            ) : null}
          </g>
        ))}
      </svg>
      {controlsOnly !== 'legend' && (
        <CurrentTime
          currentTime={currentTime ?? now}
          instance={indicatorInstance}
          showPlots={!controlsOnly}
          showZoom={controlsOnly === 'zoom' || shouldShowZoom}
        />
      )}
    </div>
  ) : null;
};
